/**
 * Date utility functions to handle timezone-safe date operations
 *
 * IMPORTANT: Never use toISOString() for date-only values as it converts to UTC
 * which can cause off-by-one day errors in different timezones.
 */

/**
 * Formats a Date object to YYYY-MM-DD string in local timezone
 * This prevents the timezone conversion issue that occurs with toISOString()
 *
 * @param {Date} date - The date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Gets today's date in YYYY-MM-DD format (local timezone)
 *
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayDate = () => {
  return formatDateForInput(new Date());
};

/**
 * Gets a date with offset from today in YYYY-MM-DD format (local timezone)
 *
 * @param {number} daysOffset - Number of days to offset (positive for future, negative for past)
 * @returns {string} Date in YYYY-MM-DD format
 */
export const getDateWithOffset = (daysOffset) => {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return formatDateForInput(date);
};

export const getDateWithYearOffset = (yearOffset = 1) => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + yearOffset);
  return formatDateForInput(date);
};

/**
 * Parses a date string and returns YYYY-MM-DD format
 * Handles both ISO strings, timestamps, and date-only strings
 *
 * @param {string} dateString - Date string to parse (can be timestamp or date)
 * @returns {string} Date in YYYY-MM-DD format or empty string if invalid
 */
export const parseDateString = (dateString) => {
  if (!dateString) return "";

  // If it's already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // If it contains time (ISO string or timestamp), extract date part
  if (dateString.includes("T") || dateString.includes(" ")) {
    return dateString.split("T")[0].split(" ")[0];
  }

  return "";
};

/**
 * Formats a date/timestamp string for display in dd/mm/yyyy format
 * Handles timezone-safe conversion to local date format
 *
 * @param {string|Date} dateString - Date or timestamp string or Date object
 * @returns {string} Formatted date string (e.g., "09/01/2026")
 */
export const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";

  let date;

  // If it's already a Date object
  if (dateString instanceof Date) {
    date = dateString;
  } else {
    // Parse the date string to YYYY-MM-DD format first
    const dateOnly = parseDateString(dateString);
    if (!dateOnly) return "";

    // Create date object at midnight local time to avoid timezone issues
    const [year, month, day] = dateOnly.split("-").map(Number);
    date = new Date(year, month - 1, day);
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};
